import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronDown, Eye, LogOut, Menu, X, Settings } from 'lucide-react';
import { ROLE_LABELS } from '@gbv/types';
import { Avatar, Logo, cn, useDesktopNotifications, useNotificationSound, useToast } from '@gbv/ui';
import { useAuth } from '../stores/auth';
import { api } from '../lib/api';
import { connectSocket, disconnectSocket, onServerEvent } from '../lib/socket';
import { FloatingChat } from './FloatingChat';
import { NAV_SECTIONS, type NavGroup } from './navigation';
import { InternalUpdateRail } from './InternalUpdateRail';
import { DueNowDialog } from './DueNowDialog';
import { useDueAlerts, useDueQueue, type DueAlert, type DueItem } from './useDueAlerts';

/** Only the two lists the deadline alerts read; the rail types the rest. */
interface RailDue {
  tasks: Array<{ id: string; title: string; dueAt: string | null }>;
  reminders: Array<{ id: string; title: string; remindAt: string }>;
}

/**
 * The employee workspace shell.
 *
 * Four columns: an icon rail for the areas someone lives in, the grouped
 * department navigation, the working area, and the internal-update rail. The
 * client being worked on is named at the top of the working area rather than
 * buried in a corner — every number on screen belongs to exactly one client,
 * and getting that wrong is the failure this platform exists to avoid.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const activeOrgId = useAuth((s) => s.activeOrgId);
  const isReadOnly = useAuth((s) => s.isReadOnly);
  const user = useAuth((s) => s.user);

  const selfId = useAuth((s) => s.user?.id);
  const toast = useToast();
  const queryClient = useQueryClient();
  const desktop = useDesktopNotifications('employee');
  const sound = useNotificationSound('employee');

  useEffect(() => {
    connectSocket();
    return () => disconnectSocket();
  }, []);

  /*
   * The deadlines this person is running towards.
   *
   * The same query the rail draws, so opening the workspace does not cost a
   * second request and both are looking at one answer. It carries only my own
   * tasks and my own reminders, which is exactly whose deadlines should
   * interrupt me.
   */
  const { data: rail } = useQuery({
    queryKey: ['internal-rail'],
    queryFn: () => api.get<RailDue>('/internal/rail'),
    enabled: Boolean(selfId),
    refetchInterval: 60_000,
  });

  const dueItems = useMemo<DueItem[]>(() => {
    if (!rail) return [];
    return [
      ...rail.tasks
        .filter((t) => t.dueAt)
        .map((t) => ({ id: t.id, title: t.title, at: t.dueAt!, kind: 'task' as const })),
      ...rail.reminders.map((r) => ({
        id: r.id,
        title: r.title,
        at: r.remindAt,
        kind: 'reminder' as const,
      })),
    ];
  }, [rail]);

  const dueQueue = useDueQueue();
  const { push: pushDue } = dueQueue;

  const onDueAlert = useCallback(
    (alert: DueAlert) => {
      const what = alert.kind === 'task' ? 'Task' : 'Reminder';

      if (alert.stage === 'now') {
        // The dialog is the alert. Sound and the desktop pop-up are for the
        // case where the tab is not the thing being looked at.
        pushDue(alert);
        sound.play('urgent');
        desktop.notify({
          title: `${what} due now`,
          body: alert.title,
          tag: `due:${alert.id}`,
          link: '/internal/tasks',
        });
        return;
      }

      const inHowLong = alert.stage === 'soon' ? 'in 5 minutes' : 'in 1 minute';
      sound.play('chime');

      const shown = desktop.notify({
        title: `${what} due ${inHowLong}`,
        body: alert.title,
        tag: `due:${alert.id}:${alert.stage}`,
        link: '/internal/tasks',
      });
      if (shown) return;

      if (alert.stage === 'imminent') toast.warning(`${what} due in 1 minute`, alert.title);
      else toast.info(`${what} due in 5 minutes`, alert.title);
    },
    [pushDue, sound, desktop, toast],
  );

  useDueAlerts(dueItems, onDueAlert);

  /**
   * Everything the server considers worth telling you about arrives on one
   * event — a task assigned, a reminder set for you, a ping, a client
   * message. Until now nothing listened to it, so all of it was dropped.
   *
   * On screen it is a toast; with the tab hidden it is a desktop
   * notification. Never both: a pop-up over a page you are already reading
   * is noise, and the hook enforces that rather than trusting callers.
   */
  useEffect(
    () =>
      onServerEvent('notification:new', (payload) => {
        // Skip what this person just did themselves. The confirmation is
        // already on screen, and a second one describing it in the third
        // person reads like somebody else acted.
        if (payload.actorId && payload.actorId === selfId) return;

        /*
         * Refresh everything this event could have changed, not just the rail.
         *
         * A direct message, an internal mail and an assigned task all arrive
         * on this one event, and it arrives in well under a second. Only the
         * rail was being refreshed, so the thread someone was actually looking
         * at sat there until its own poll came round — fifteen seconds for a
         * chat, sixty for the unread badge, and never for the mail list. That
         * is the whole of "messages arrive late": the delivery was instant and
         * the screen was on a timer.
         */
        for (const key of [
          ['internal-rail'],
          ['internal'],
          ['personal-chat'],
          ['personal-unread'],
          ['personal-thread'],
          ['emp-chat-list'],
          ['emp-chat-thread'],
          ['chat-unread'],
        ]) {
          void queryClient.invalidateQueries({ queryKey: key });
        }

        // Whether it lands as a desktop pop-up or a toast, it makes the same
        // sound — the point of the sound is that you did not have to be
        // looking, and which surface it used is not something you chose.
        sound.play('chime');

        const shown = desktop.notify({
          title: payload.title,
          body: payload.body,
          link: payload.link,
          // Repeats of the same thing replace rather than stack.
          tag: payload.id,
        });

        if (!shown) toast.info(payload.title, payload.body);
      }),
    [queryClient, desktop, toast, selfId, sound],
  );

  return (
    <div className="flex h-screen overflow-hidden bg-surface-page">
      {/* No icon rail. Everything it shortcut to is already in the department
          nav beside it, so it was 52px spent on a duplicate. */}
      <DepartmentNav className="hidden lg:flex" />

      <div className="flex min-w-0 flex-1 flex-col">
        {isReadOnly && (
          <div
            role="status"
            className="flex items-center gap-2 bg-bee px-4 py-1.5 text-xs font-semibold text-ink"
          >
            <Eye size={13} />
            Viewing as {user?.name ?? 'this employee'} — read only. Nothing you do here will save.
          </div>
        )}

        <WorkingAreaHeader onOpenMobileNav={() => setMobileNavOpen(true)} />

        <main
          key={activeOrgId}
          className="min-h-0 flex-1 overflow-y-auto p-content-sm md:p-content"
        >
          {children}
        </main>
      </div>

      <InternalUpdateRail />

      {/* Shell root, not inside the nav column: the desktop nav is `hidden`
          below lg, and a launcher inside it would vanish on exactly the phone
          screens where a floating button matters most — while the drawer copy
          would sit under its own overlay and mount a second widget. */}
      <FloatingChat />

      <DueNowDialog
        alert={dueQueue.current}
        waiting={dueQueue.waiting}
        onDismiss={dueQueue.dismiss}
      />

      {mobileNavOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-ink/50"
            onClick={() => setMobileNavOpen(false)}
            role="presentation"
          />
          <DepartmentNav
            className="absolute left-0 top-0 flex h-full"
            onNavigate={() => setMobileNavOpen(false)}
            onClose={() => setMobileNavOpen(false)}
          />
        </div>
      )}
    </div>
  );
}

function DepartmentNav({
  className,
  onNavigate,
  onClose,
}: {
  className?: string;
  onNavigate?: () => void;
  onClose?: () => void;
}) {
  const can = useAuth((s) => s.can);
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const membership = useAuth((s) => s.activeMembership());

  // Groups with nothing this person can reach do not appear at all.
  const visible = (gs: NavGroup[]): NavGroup[] =>
    gs
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (i) => (!i.feature || can(i.feature)) && (!i.anyFeature || i.anyFeature.some(can)),
        ),
      }))
      .filter((g) => g.items.length > 0);

  const sections = NAV_SECTIONS.map((s) => ({ ...s, groups: visible(s.groups) })).filter(
    (s) => s.groups.length > 0,
  );

  return (
    <div
      className={cn('w-[212px] shrink-0 flex-col border-r border-white/5 bg-auth-panel', className)}
    >
      <div className="flex h-topbar shrink-0 items-center gap-2.5 px-4">
        {/* The mark used to live in the icon rail; it moves here so the brand
            is still the first thing in the column. */}
        <Logo size={24} showWordmark wordmarkSize={18} />
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation"
            className="ml-auto rounded p-1 text-white/40 hover:text-white"
          >
            <X size={16} />
          </button>
        )}
      </div>

      <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto px-2 pb-4">
        {sections.map((section, sectionIndex) => (
          <div
            key={section.section}
            className={cn(
              sectionIndex > 0 &&
                // A rule and a gold-tinted heading, not just more spacing: the
                // whole point is that crossing this line changes who sees your
                // work, and spacing alone reads as "more of the same list".
                'mt-5 border-t border-white/10 pt-4',
            )}
          >
            <div className="mb-2 px-2">
              <p
                className={cn(
                  'font-mono text-[9px] uppercase tracking-[0.18em]',
                  section.section === 'internal' ? 'text-auth-gold/70' : 'text-white/35',
                )}
              >
                {section.label}
              </p>
              <p className="mt-0.5 text-[9.5px] leading-tight text-white/25">{section.hint}</p>
            </div>

            {section.groups.map((group, index) => (
              <div key={group.department ?? `top-${index}`} className={cn(index > 0 && 'mt-4')}>
                {group.department && (
                  <p className="px-2 pb-1 font-mono text-[9px] uppercase tracking-[0.16em] text-white/25">
                    {group.department}
                  </p>
                )}

                <div className="flex flex-col gap-0.5">
                  {group.items.map((item) => (
                    <NavLink
                      key={`${group.department ?? 'top'}-${item.to}`}
                      to={item.to}
                      end={item.to === '/'}
                      onClick={onNavigate}
                      className={({ isActive }) =>
                        cn(
                          'group flex items-center gap-2 rounded-control px-2 py-1.5 text-[12.5px] transition-colors',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-auth-gold',
                          isActive
                            ? 'bg-auth-gold/15 font-semibold text-auth-gold'
                            : 'text-white/45 hover:bg-white/5 hover:text-white/85',
                        )
                      }
                    >
                      <item.icon size={14} className="shrink-0" />
                      <span className="truncate">{item.label}</span>
                      {item.planned && (
                        <span className="ml-auto shrink-0 font-mono text-[8px] uppercase tracking-wider text-white/20">
                          soon
                        </span>
                      )}
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="shrink-0 border-t border-white/5 px-3 py-3">
        <div className="flex items-center gap-2">
          <Avatar name={user?.name ?? '?'} src={user?.avatarUrl} size="sm" />
          <div className="min-w-0">
            <p className="truncate text-[12px] font-semibold text-white/85">{user?.name}</p>
            <p className="truncate font-mono text-[9px] uppercase tracking-wider text-white/30">
              {membership ? ROLE_LABELS[membership.role] : 'No client assigned'}
            </p>
          </div>
        </div>
        <NavLink
          to="/account"
          className={({ isActive }) =>
            cn(
              'mt-2 flex items-center gap-1.5 text-[11px] font-semibold transition-colors',
              isActive ? 'text-white' : 'text-white/40 hover:text-white/80',
            )
          }
        >
          <Settings size={12} />
          Settings
        </NavLink>
        <button
          type="button"
          onClick={() => void logout()}
          className="mt-2 flex items-center gap-1.5 text-[11px] font-semibold text-white/40 transition-colors hover:text-danger"
        >
          <LogOut size={12} />
          Log out
        </button>
      </div>
    </div>
  );
}

/**
 * The working area's own header. The client chip is the most important control
 * on the screen — it decides whose data everything below belongs to.
 */
function WorkingAreaHeader({ onOpenMobileNav }: { onOpenMobileNav: () => void }) {
  const memberships = useAuth((s) => s.memberships);
  const activeOrgId = useAuth((s) => s.activeOrgId);
  const switchOrg = useAuth((s) => s.switchOrg);
  const queryClient = useQueryClient();
  const location = useLocation();

  const active = memberships.find((m) => m.orgId === activeOrgId);

  function handleSelect(orgId: string): void {
    if (orgId === activeOrgId) return;
    switchOrg(orgId);
    // Drop every cached query. Showing one client's numbers under another
    // client's name, even for a frame, is the failure to avoid.
    queryClient.clear();
  }

  const title = titleFor(location.pathname);

  return (
    <header className="flex h-topbar shrink-0 items-center gap-3 border-b border-line bg-white px-4 lg:px-6">
      <button
        type="button"
        onClick={onOpenMobileNav}
        aria-label="Open navigation"
        className="rounded-control p-1.5 text-ink-muted transition-colors hover:bg-surface-sunken lg:hidden"
      >
        <Menu size={18} />
      </button>

      {/* Dark on a light bar, so the one control that decides whose data is on
          screen cannot be mistaken for chrome. Every figure below it belongs to
          whichever client this names. */}
      {memberships.length === 0 ? (
        <span className="text-sm text-ink-muted">No client assigned</span>
      ) : memberships.length === 1 ? (
        <span className="flex items-center gap-2 rounded-control bg-auth-panel px-3.5 py-2 text-[12.5px] font-semibold text-white">
          <span className="font-mono text-[10px] uppercase tracking-wider text-white/45">
            Client
          </span>
          {active?.orgName}
        </span>
      ) : (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger
            className={cn(
              'flex items-center gap-2 rounded-control bg-auth-panel px-3.5 py-2',
              'text-[12.5px] font-semibold text-white transition-colors hover:bg-auth-panel/85',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-auth-gold',
            )}
          >
            <span className="font-mono text-[10px] uppercase tracking-wider text-white/45">
              Client
            </span>
            <span className="max-w-[16rem] truncate">{active?.orgName ?? 'Choose'}</span>
            <ChevronDown size={13} className="text-white/50" />
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="start"
              sideOffset={6}
              className="z-50 w-72 overflow-hidden rounded-card border border-line bg-white shadow-overlay"
            >
              <p className="border-b border-line px-3 py-2 font-mono text-[9px] uppercase tracking-[0.16em] text-ink-soft">
                {memberships.length} clients assigned to you
              </p>
              <div className="max-h-80 overflow-y-auto py-1">
                {memberships.map((m) => (
                  <DropdownMenu.Item
                    key={m.orgId}
                    onSelect={() => handleSelect(m.orgId)}
                    className="flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm outline-none data-[highlighted]:bg-surface-sunken"
                  >
                    <Avatar name={m.orgName} src={m.orgLogoUrl} size="sm" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-semibold text-ink">{m.orgName}</span>
                      <span className="block font-mono text-[10px] uppercase tracking-wider text-ink-soft">
                        {ROLE_LABELS[m.role]}
                      </span>
                    </span>
                    {m.orgId === activeOrgId && (
                      <Check size={14} className="shrink-0 text-go-dark" />
                    )}
                  </DropdownMenu.Item>
                ))}
              </div>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      )}

      <span className="ml-auto truncate font-mono text-[10px] uppercase tracking-[0.16em] text-ink-soft">
        {title}
      </span>
    </header>
  );
}

function titleFor(pathname: string): string {
  if (pathname === '/') return 'Working area';
  const segment = pathname.split('/')[1] ?? '';
  return segment.replace(/-/g, ' ') || 'Working area';
}
